import React, { useState } from 'react';
import { Experience } from '../../data/go2LegendeMock';
import { Star, Clock, Sparkles } from 'lucide-react';

interface ExperienceMarketplaceProps {
  experiences: Experience[];
}

type CategoryFilter = 'all' | 'guide' | 'food' | 'adventure' | 'culture' | 'nature' | 'nightlife';

const CATEGORY_LABELS: Record<CategoryFilter, string> = {
  all: 'Sve',
  guide: 'Vodič',
  food: 'Hrana',
  adventure: 'Avantura',
  culture: 'Kultura',
  nature: 'Priroda',
  nightlife: 'Noćni Život',
};

export function ExperienceMarketplace({ experiences }: ExperienceMarketplaceProps) {
  const [activeCategory, setActiveCategory] = useState<CategoryFilter>('all');

  const filteredExperiences = activeCategory === 'all'
    ? experiences
    : experiences.filter((exp) => exp.category === activeCategory);

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2 mb-2">
          <Sparkles className="w-6 h-6 text-brand-500" />
          Iskustva & Dodaci
        </h2>
        <p className="text-gray-600">Obogati putovanje, zaradi više bodova</p>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {(Object.keys(CATEGORY_LABELS) as CategoryFilter[]).map((category) => (
          <button
            key={category}
            onClick={() => setActiveCategory(category)}
            className={`px-4 py-2 rounded-full font-medium whitespace-nowrap transition ${
              activeCategory === category
                ? 'bg-brand-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {CATEGORY_LABELS[category]}
          </button>
        ))}
      </div>

      {/* Experience Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredExperiences.map((experience) => (
          <div
            key={experience.id}
            className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition relative"
          >
            {/* Popular Badge */}
            {experience.popular && (
              <div className="absolute top-3 right-3 z-10">
                <span className="bg-brand-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                  Popularno
                </span>
              </div>
            )}

            {/* Card Content */}
            <div className="p-5">
              {/* Emoji */}
              <div className="text-5xl mb-3">{experience.emoji}</div>

              {/* Destination Tag */}
              <div className="mb-2">
                <span className="inline-block bg-gray-100 text-gray-700 text-xs font-medium px-3 py-1 rounded-full">
                  {experience.destination}
                </span>
              </div>

              {/* Title */}
              <h3 className="text-lg font-bold text-gray-900 mb-2">{experience.title}</h3>

              {/* Description */}
              <p className="text-sm text-gray-600 line-clamp-2 mb-3">{experience.description}</p>

              {/* Rating & Duration */}
              <div className="flex items-center gap-4 mb-3">
                {/* Rating */}
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  <span className="text-sm font-medium text-gray-900">{experience.rating}</span>
                </div>

                {/* Duration */}
                <div className="flex items-center gap-1 text-gray-600">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm">{experience.duration}</span>
                </div>
              </div>

              {/* Price */}
              <div className="mb-2">
                <span className="text-xl font-bold text-gray-900">od {experience.price} EUR</span>
              </div>

              {/* Points Bonus */}
              <div className="mb-2">
                <span className="text-sm font-medium text-brand-500">
                  +{experience.bonusPoints} bonus bodova
                </span>
              </div>

              {/* Multiplier Badge */}
              {experience.pointsMultiplier > 1 && (
                <div className="mb-4">
                  <span className="inline-block bg-go2-yellow text-gray-900 text-xs font-bold px-3 py-1 rounded-full">
                    {experience.pointsMultiplier}x bodovi
                  </span>
                </div>
              )}

              {/* CTA Button */}
              <button className="w-full px-4 py-2.5 border-2 border-brand-500 text-brand-500 font-medium rounded-full hover:bg-brand-50 transition">
                Dodaj na putovanje
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredExperiences.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">Nema dostupnih iskustava u ovoj kategoriji</p>
        </div>
      )}
    </div>
  );
}
